import { Link } from '@tanstack/react-router';
import { PreviewSheet } from '../../PreviewSheet';
import { ExternalLinkIcon } from '../../icons';
import type { Inventory } from '../types';
import { useTranslation } from '@hooks/useTranslation';
import { DefaultInventorySection } from './DefaultInventorySection';
import { InventoryStatusDropdown } from '../../InventoryDetails/InventoryStatusDropdown';
import { InventoryOverviewSection } from '../../InventoryDetails/InventoryOverviewSection';
import { InventoryPreviewFooter } from '../../InventoryDetails/InventoryPreviewFooter';
import { Button } from '@/components/ui/button';
import { useNavigate } from '@tanstack/react-router';

interface InventoryDetailSheetProps {
  inventory: Inventory | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** RBAC: when false, hide write affordances (status toggle + edit). */
  canWrite?: boolean;
}

function SheetHeader({
  inventory,
  onClose,
  canWrite,
}: {
  inventory: Inventory;
  onClose?: () => void;
  canWrite: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-3">
        <Link
          to="/inventories/$id/details"
          params={{ id: String(inventory.id) }}
          onClick={() => onClose?.()}
          className="text-grey hover:text-off-black transition-colors"
        >
          <ExternalLinkIcon />
        </Link>
        <span className="font-inter text-lg font-semibold text-off-black">
          {t('index.header.view_item_details')}
        </span>
      </div>

      {canWrite && (
        <InventoryStatusDropdown inventoryId={inventory.id} active={inventory.active} />
      )}
    </div>
  );
}

export function InventoryDetailSheet({
  inventory,
  open,
  onOpenChange,
  canWrite = true,
}: InventoryDetailSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasInventoryConfigs = inventory?.inventory_configurations && inventory.inventory_configurations.length > 0;

  return (
    <PreviewSheet
      open={open && inventory !== null}
      onOpenChange={onOpenChange}
      header={inventory ? <SheetHeader inventory={inventory} onClose={() => onOpenChange(false)} canWrite={canWrite} /> : undefined}
      footerClassName="justify-end"
      footer={
        inventory && canWrite ? (
          <Button
            variant="outline-subtle"
            onClick={() => {
              onOpenChange(false);
              navigate({ to: '/inventories/$id/edit', params: { id: String(inventory.id) } });
            }}
          >
            {t('index.footer.edit')}
          </Button>
        ) : undefined
      }
    >
      {inventory && (
        <>
          <InventoryOverviewSection inventory={inventory} />
          {hasInventoryConfigs && (
            <DefaultInventorySection configurations={inventory.inventory_configurations} inventoryName={inventory.name} showTooltip={true} />
          )}
        </>
      )}
    </PreviewSheet>
  );
}
